import React, { useState, useEffect } from 'react';
import { Edit, Trash2, Plus, Check, X, ChevronUp, ChevronDown } from 'lucide-react';
import apiClient from '../apis/apiClient';
import './AdminMenu.css';

const api = apiClient;

const AdminMenu = () => {
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [newItem, setNewItem] = useState({
    dish: '',
    category: '',
    categoryId: '',
    price: '',
    available: true,
    description: '',
    dishimageUrl: ''
  });
  const [newCategory, setNewCategory] = useState('');
  const [editId, setEditId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: 'dish', direction: 'asc' });
  const [loading, setLoading] = useState(true);

  // Fixed category order
  const CATEGORY_ORDER = [
    'SUPPEN',
    'SALATE',
    'VORSPEISEN',
    'INDISCHE BROTSPEZIALITÄTEN',
    'VEGETARISCH',
    'HÄHNCHENGERICHTE',
    'LAMMGERICHTE',
    'FISCHGERICHTE',
    'PASTA',
    'TANDOORI SPEZIALITÄTEN',
    'REIS SPEZIALITÄTEN',
    'NACHSPEISEN',
    'INDISCHE GETRÄNKE'
  ];

  // Map real category names (with descriptions) to base names
  const CATEGORY_MAP = {
    'SUPPEN': 'SUPPEN',
    'SALATE': 'SALATE',
    'VORSPEISEN': 'VORSPEISEN',
    'PASTA': 'PASTA',
    'NACHSPEISEN': 'NACHSPEISEN',
    'FISCHGERICHTE INKLUSIVE BASMATI REIS': 'FISCHGERICHTE',
    'HÄHNCHENGERICHTE INKLUSIVE BASMATI REIS': 'HÄHNCHENGERICHTE',
    'INDISCHE BROTSPEZIALITÄTEN': 'INDISCHE BROTSPEZIALITÄTEN',
    'INDISCHE GETRÄNKE': 'INDISCHE GETRÄNKE',
    'LAMMGERICHTE INKLUSIVE BASMATI REIS': 'LAMMGERICHTE',
    'REIS SPEZIALITÄTEN': 'REIS SPEZIALITÄTEN',
    'TANDOORI SPEZIALITÄTEN TANDOORI GERICHTE WERDEN ZUBEREITET, INDEM SIE STUNDENLANG IN AROMATISCHEN MARINADEN EINGELEGT UND ANSCHLIESSEND IM TANDOORI OFEN GEGRILLT WERDEN. MIT SALAT, NAAN UND BASMATI REIS': 'TANDOORI SPEZIALITÄTEN',
    'VEGETARISCH INKLUSIVE BASMATI REIS': 'VEGETARISCH'
  };

  const normalize = (str) =>
    str.normalize('NFD').replace(/\p{Diacritic}/gu, '').toUpperCase().trim();

  const sortCategories = (categories) => {
    return [...categories].sort((a, b) => {
      const baseA = CATEGORY_MAP[normalize(a.name)] || normalize(a.name);
      const baseB = CATEGORY_MAP[normalize(b.name)] || normalize(b.name);

      const indexA = CATEGORY_ORDER.indexOf(baseA);
      const indexB = CATEGORY_ORDER.indexOf(baseB);

      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      return a.name.localeCompare(b.name);
    });
  };

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      window.location.href = '/login';
    } else {
      fetchData();
    }
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [categoriesRes, menuItemsRes] = await Promise.all([
        api.get('/categories'),
        api.get('/dishes')
      ]);

      if (categoriesRes.data.length === 0) {
        throw new Error('No categories found - please create at least one category first');
      }

      const sortedCategories = sortCategories(categoriesRes.data);
      setCategories(sortedCategories);
      setMenuItems(menuItemsRes.data);

      setNewItem(prev => ({
        ...prev,
        category: sortedCategories[0]?.name || '',
        categoryId: sortedCategories[0]?.id || ''
      }));
    } catch (error) {
      console.error('Error fetching data:', error);
      setError(error.message || 'Failed to load data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewItem({
      ...newItem,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleCategoryChange = (e) => {
    const selectedCategory = categories.find(cat => cat.name === e.target.value);
    if (!selectedCategory) {
      setError('Selected category not found');
      return;
    }
    setNewItem({
      ...newItem,
      category: e.target.value,
      categoryId: selectedCategory.id
    });
  };

  const validateItem = () => {
    if (!newItem.dish.trim()) {
      setError('Dish name is required');
      return false;
    }
    if (!newItem.price || isNaN(parseFloat(newItem.price))) {
      setError('Valid price is required');
      return false;
    }
    if (parseFloat(newItem.price) <= 0) {
      setError('Price must be greater than 0');
      return false;
    }
    if (!newItem.categoryId) {
      setError('Please select a valid category');
      return false;
    }
    return true;
  };

  const handleAddItem = async () => {
    setError('');
    setSuccess('');

    if (!validateItem()) return;

    if (!editId) {
      const duplicate = menuItems.find(
        item => item.dishname.trim().toLowerCase() === newItem.dish.trim().toLowerCase()
      );
      if (duplicate) {
        setError('Dish with the same name already exists!');
        return;
      }
    }

    try {
      const menuItemData = {
        dishname: newItem.dish,
        dishprice: parseFloat(newItem.price),
        dishdescription: newItem.description,
        available: newItem.available,
        dishimageUrl: newItem.dishimageUrl,
        category: { id: newItem.categoryId }
      };

      console.log('🔄 Sending Update to Backend:', menuItemData);

      if (editId) {
        const response = await api.put(`/dishes/${editId}`, menuItemData);
        setMenuItems(prev => prev.map(item =>
          item.dishid === editId ? response.data : item
        ));
        setSuccess('Item updated successfully!');
      } else {
        const response = await api.post('/dishes', menuItemData);
        setMenuItems(prev => [...prev, response.data]);
        setSuccess('Item added successfully!');
      }

      resetForm();
    } catch (error) {
      console.error('❌ Error saving item:', error);
      console.error('Error details:', error.response?.data);
      setError(error.response?.data?.message || 'Failed to save item. Please check all fields and try again.');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await api.delete(`/dishes/${id}`);
        setMenuItems(prev => prev.filter(item => item.dishid !== id));
        if (editId === id) {
          resetForm();
        }
        setSuccess('Item deleted successfully!');
      } catch (error) {
        console.error('Error deleting item:', error);
        setError('Failed to delete item. Please try again.');
      }
    }
  };

  const handleEdit = (item) => {
    setEditId(item.dishid);
    setNewItem({
      dish: item.dishname,
      category: item.category?.name || '',
      categoryId: item.category?.id || '',
      price: item.dishprice.toString(),
      available: item.available,
      description: item.dishdescription || '',
      dishimageUrl: item.dishimageUrl || ''
    });
    setError('');
    setSuccess('');
  };

  const handleAddCategory = async () => {
    const cat = newCategory.trim();
    if (!cat) {
      setError('Category name is required');
      return;
    }

    try {
      const response = await api.post('/categories', { name: cat });
      const newCategories = sortCategories([...categories, response.data]);
      setCategories(newCategories);
      setNewCategory('');
      setError('');
      setSuccess('Category added successfully!');

      setNewItem(prev => ({
        ...prev,
        category: response.data.name,
        categoryId: response.data.id
      }));
    } catch (error) {
      console.error('Error adding category:', error);
      setError(error.response?.data?.message || 'Failed to add category. It may already exist.');
    }
  };

  const handleDeleteCategory = async (category) => {
    if (menuItems.some(item => item.category?.id === category.id || item.category_id === category.id)) {
      setError('Cannot delete category with items. Please move or delete items first.');
      return;
    }

    if (window.confirm(`Are you sure you want to delete the "${category.name}" category?`)) {
      try {
        await api.delete(`/categories/${category.id}`);
        setCategories(prev => sortCategories(prev.filter(c => c.id !== category.id)));
        setSuccess('Category deleted successfully!');
      } catch (error) {
        console.error('Error deleting category:', error);
        setError('Failed to delete category. Please try again.');
      }
    }
  };

  const resetForm = () => {
    setEditId(null);
    setNewItem({
      dish: '',
      category: categories[0]?.name || '',
      categoryId: categories[0]?.id || '',
      price: '',
      available: true,
      description: '',
      dishimageUrl: ''
    });
    setTimeout(() => setSuccess(''), 3000);
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedItems = (items) => {
    const sortableItems = [...items];
    if (sortConfig.key) {
      sortableItems.sort((a, b) => {
        const aValue = sortConfig.key.includes('.')
          ? sortConfig.key.split('.').reduce((o, i) => o[i], a)
          : a[sortConfig.key];

        const bValue = sortConfig.key.includes('.')
          ? sortConfig.key.split('.').reduce((o, i) => o[i], b)
          : b[sortConfig.key];

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  };

  const filteredItems = menuItems.filter(item =>
  (item.dishname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.dishdescription?.toLowerCase().includes(searchTerm.toLowerCase())
  ));

  const groupedItems = sortCategories(categories).reduce((acc, category) => {
    acc[category.name] = filteredItems.filter(item =>
      item.category?.id === category.id || item.category_id === category.id
    );
    return acc;
  }, {});

  const getSortIndicator = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'asc'
        ? <ChevronUp size={16} />
        : <ChevronDown size={16} />;
    }
    return null;
  };

  if (loading) {
    return (
      <div className="loadingContainer">
        <div className="spinner"></div>
        <p>Loading menu data...</p>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="header">
        <h2 className="title">🍽️ Menüverwaltung</h2>
        <div className="searchContainer">
          <input
            type="text"
            placeholder="Menüelemente durchsuchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="searchInput"
          />
        </div>
      </div>

      <div className="card">
        <h3 className="sectionTitle">{editId ? 'Edit Menu Item' : 'Neuen Menüpunkt hinzufügen'}</h3>

        <div className="formGrid">
          <div className="formGroup">
            <label className="label">Name des Gerichts</label>
            <input
              type="text"
              name="dish"
              placeholder="Geben Sie den Namen des Gerichts ein"
              value={newItem.dish}
              onChange={handleInputChange}
              className="input"
              required
            />
          </div>

          <div className="formGroup">
            <label className="label">Kategorie</label>
            <div className="selectContainer">
              <select
                name="category"
                value={newItem.category}
                onChange={handleCategoryChange}
                className="select"
                required
              >
                {sortCategories(categories).map(category => (
                  <option key={category.id} value={category.name}>{category.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="formGroup">
            <label className="label">Preis (€)</label>
            <input
              type="number"
              name="price"
              placeholder="0.00"
              value={newItem.price}
              onChange={handleInputChange}
              min="0"
              step="0.01"
              className="input"
              required
            />
          </div>

          <div className="formGroup">
            <label className="label">Beschreibung</label>
            <input
              type="text"
              name="description"
              placeholder="Beschreibung eingeben (optional)"
              value={newItem.description}
              onChange={handleInputChange}
              className="input"
            />
          </div>

          {/* <div className="formGroup">
            <label className="checkboxLabel">
              <input
                type="checkbox"
                name="available"
                checked={newItem.available}
                onChange={handleInputChange}
                className="checkbox"
              />
              Verfügbar
            </label>
          </div> */}

          <div className="buttonGroup">
            <button
              onClick={handleAddItem}
              className="primaryButton"
            >
              {editId ? (
                <>
                  <Edit size={16} className="button-icon" />
                  Update
                </>
              ) : (
                <>
                  <Plus size={16} className="button-icon" />
                  Artikel hinzufügen
                </>
              )}
            </button>

            {editId && (
              <button
                onClick={resetForm}
                className="secondaryButton"
              >
                <X size={16} className="button-icon" />
                Stornieren
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="card">
        <h3 className="sectionTitle">Kategorien verwalten</h3>
        <div className="categoryForm">
          <input
            type="text"
            placeholder="Neuer Kategoriename"
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            className="categoryInput"
          />
          <button
            onClick={handleAddCategory}
            className="smallButton"
          >
            <Plus size={16} className="button-icon" />
            Kategorie hinzufügen
          </button>
        </div>
        <div className="categoryList">
          {sortCategories(categories).map(category => (
            <div key={category.id} className="categoryItem">
              <span>{category.name}</span>
              <button
                onClick={() => handleDeleteCategory(category)}
                className="deleteCategoryButton"
                disabled={menuItems.some(item => item.category?.id === category.id || item.category_id === category.id)}
                title={menuItems.some(item => item.category?.id === category.id || item.category_id === category.id)
                  ? "Cannot delete category with items"
                  : "Delete category"}
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {error && (
        <div className="alertError">
          <X size={18} style={{ marginRight: '8px' }} />
          {error}
        </div>
      )}

      {success && (
        <div className="alertSuccess">
          <Check size={18} style={{ marginRight: '8px' }} />
          {success}
        </div>
      )}

      <div className="menuSection">
        {sortCategories(categories).map(category => (
          <div key={category.id} className="categorySection">
            <div
              className="categoryHeader"
              onClick={() => toggleCategory(category.name)}
            >
              <h3 style={{ margin: 0, flex: 1 }}>{category.name}</h3>
              <span className="toggleIcon">
                {expandedCategories[category.name] ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </span>
              <span className="itemCount">
                {groupedItems[category.name]?.length || 0} item{groupedItems[category.name]?.length !== 1 ? 's' : ''}
              </span>
            </div>

            {expandedCategories[category.name] !== false && (
              <div className="tableContainer">
                <table className="table">
                  <thead>
                    <tr>
                      <th
                        className="th"
                        onClick={() => requestSort('dishname')}
                      >
                        <div className="thContent">
                          Gericht {getSortIndicator('dishname')}
                        </div>
                      </th>
                      <th
                        className="th"
                        onClick={() => requestSort('dishprice')}
                      >
                        <div className="thContent">
                          Preis (€) {getSortIndicator('dishprice')}
                        </div>
                      </th>
                      <th
                        className="th"
                        onClick={() => requestSort('available')}
                      >
                        {/* <div className="thContent">
                          Verfügbar {getSortIndicator('available')}
                        </div> */}
                      </th>
                      <th className="th" style={{ width: '180px' }}>Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedItems(groupedItems[category.name] || []).map(item => (
                      <tr key={item.dishid}>
                        <td className="td">{item.dishname}</td>
                        <td className="td">{item.dishprice.toFixed(2)}</td>
                        <td className="td">
                          {/* <span className={`status ${item.available ? 'status-available' : 'status-unavailable'}`}>
                            {item.available ? 'Yes' : 'No'}
                          </span> */}
                        </td>
                        <td className="td">
                          <div className="actionButtons">
                            <button
                              onClick={() => handleEdit(item)}
                              className="iconButton editButton"
                              title="Edit"
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              onClick={() => handleDelete(item.dishid)}
                              className="iconButton deleteButton"
                              title="Delete"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {(!groupedItems[category.name] || groupedItems[category.name].length === 0) && (
                      <tr>
                        <td colSpan="4" className="tdEmpty">Keine Artikel in dieser Kategorie</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminMenu;
